// ── Identity colour — one person, one hue, on every screen ───────────────────
// The redesign gives the roster seven warm hues (--id-1..7). A person's hue is
// their INDEX in the org's profile list ordered by full_name, mod 7 — not a
// hash of the name, which collided (two people the same colour two rows apart)
// and not an index into whatever subset a screen happens to have fetched,
// which drifts (the same foreman came out blue on one tab and green on the
// next). Three screens read this: the Submissions feed's avatars, the Home
// crew stack, and the Schedule's bars.
//
// setRoster() takes the FULL profile list, so every caller must pass the same
// unfiltered, name-ordered query — that is what makes the answer stable.

const HUES = 7;
// Literal twins of --id-1..7 in css/redesign-tokens.css. A <canvas> and a PDF
// cannot resolve var(), so colorLiteral() reads the live token and falls back
// to these; the CSS file stays the one place the palette is edited.
const FALLBACK = ['#C2410C', '#1D6FA8', '#B03A6E', '#2F7D5B', '#6B4FA8', '#A8560A', '#0F7B84'];

let _index = {};   // full_name (lower-cased, trimmed) -> 0..6

const key = name => String(name || '').trim().toLowerCase();

export function setRoster(profiles) {
  _index = {};
  (profiles || []).forEach((p, i) => {
    const k = key(p && (p.full_name ?? p.name));
    if (k && !(k in _index)) _index[k] = i % HUES;
  });
  return _index;
}

// Index 6 is the deliberate landing spot for a name the roster has never seen
// (a historical entry with no profile row): consistently coloured rather than
// randomly.
export function indexOf(name) {
  const k = key(name);
  return (k && k in _index) ? _index[k] : HUES - 1;
}

export function colorVar(name)     { return `var(--id-${indexOf(name) + 1})`; }
export function colorFallback(name) { return FALLBACK[indexOf(name)]; }

// Resolved literal, for the two consumers that cannot take a var(): the
// manpower canvas and the schedule PDF.
export function colorLiteral(name) {
  const i = indexOf(name);
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(`--id-${i + 1}`).trim();
    if (v) return v;
  } catch (_) { /* no document (tests) — fall through */ }
  return FALLBACK[i];
}

export function initials(name) {
  const s = String(name || '').trim().split(/\s+/).map(w => w[0] || '').join('')
    .replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();
  return s || '?';
}
